<script setup lang="ts" generic="T extends string">
import { useI18n } from 'vue-i18n'
import type { PartLine } from '../model/spec'
import CountSelect from './CountSelect.vue'
import FormRow from './FormRow.vue'

/** "2 × DDR5 module": a count and a generic part type, each type with its wattage. */
const props = defineProps<{
  modelValue: PartLine<T>
  id: string
  label: string
  types: readonly T[]
  /** Catalog prefix for the type names: `${labelPrefix}.${type}`. */
  labelPrefix: string
  watts: Readonly<Record<T, number>>
  max: number
  hideLabel?: boolean
}>()
const emit = defineEmits<{ 'update:modelValue': [PartLine<T>] }>()

const { t, n } = useI18n()

function onType(event: Event): void {
  const target = event.target
  if (!(target instanceof HTMLSelectElement)) return
  const type = props.types.find((candidate) => candidate === target.value)
  if (type) emit('update:modelValue', { ...props.modelValue, type })
}
</script>

<template>
  <FormRow :label="label" :for-id="id" :hide-label="hideLabel">
    <CountSelect
      :id="`${id}-count`"
      :model-value="modelValue.count"
      :max="max"
      :label="t('form.quantity', { part: label })"
      @update:model-value="(count) => emit('update:modelValue', { ...modelValue, count })"
    />
    <span class="form-row__muted" aria-hidden="true">×</span>
    <select :id="id" :value="modelValue.type" @change="onType">
      <option v-for="type in types" :key="type" :value="type">
        {{ t(`${labelPrefix}.${type}`) }} — {{ t('form.watts', { value: n(watts[type]) }) }}
      </option>
    </select>
  </FormRow>
</template>
